import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import { cn } from "@/core/utils/utils";

/** Representative title widths so skeleton rows don't all look identical. */
const SKELETON_TITLE_WIDTHS = ["w-64", "w-52", "w-56", "w-48"];

/**
 * Loading placeholder that mirrors InvoiceGroupCard 1:1 — same card chrome and
 * header padding, a two-line identity with the amount pinned to line two, and
 * divided rows ending in the pill + ✕ controls — so the queue doesn't reflow
 * when real data lands.
 */
export const InvoiceGroupCardSkeleton = ({ rows = 3 }: { rows?: number }) => (
  <Stack
    gap="none"
    fullWidth
    className="overflow-hidden rounded-lg border border-stroke-tertiary bg-card pb-2"
  >
    {/* Header */}
    <Stack
      direction="row"
      justify="between"
      align="center"
      gap="md"
      fullWidth
      className="bg-bg-secondary/50 px-6 py-4"
    >
      <Stack gap="none" className="min-w-0 flex-1">
        {/* Line 1: vendor + status badge */}
        <Stack direction="row" gap="sm" align="center">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-5 w-20 rounded-full" />
        </Stack>
        {/* Line 2: metadata, then the divider + amount pinned to its end */}
        <Stack direction="row" gap="md" align="center" className="mt-1.5">
          <Skeleton className="h-3.5 w-64" />
          <span className="h-5 w-px bg-stroke-tertiary" aria-hidden />
          <Skeleton className="h-3.5 w-16" />
        </Stack>
      </Stack>
      {/* Mirrors a row's [pill][gap][✕ slot] trailing structure. */}
      <Stack
        direction="row"
        gap="sm"
        align="center"
        justify="end"
        className="-mr-2 flex-none"
      >
        <Skeleton className="h-8 w-[7.5rem] rounded-full" />
        <span className="h-8 w-8 flex-none" aria-hidden />
      </Stack>
    </Stack>

    {/* Action rows */}
    {Array.from({ length: rows }).map((_, i) => (
      <Stack key={i} gap="none" fullWidth className="px-6">
        <Stack
          direction="row"
          gap="md"
          align="start"
          justify="between"
          fullWidth
          className={cn("py-3", {
            "border-t border-stroke-tertiary": i > 0,
          })}
        >
          <Stack gap="xs" className="min-w-0 flex-1">
            <Skeleton
              className={cn(
                "h-4",
                SKELETON_TITLE_WIDTHS[i % SKELETON_TITLE_WIDTHS.length],
              )}
            />
            <Skeleton className="h-3 w-32" />
          </Stack>
          <Stack
            direction="row"
            gap="sm"
            align="center"
            className="-mr-2 flex-none"
          >
            <Skeleton className="h-8 w-[7.5rem] rounded-full" />
            <Skeleton className="h-8 w-8 rounded-full" />
          </Stack>
        </Stack>
      </Stack>
    ))}
  </Stack>
);
